import React, { useCallback, useMemo } from 'react';
import AppTable from 'components/AppTable';
import ModalBusyContainer from 'components/ModalBusyContainer';
import PageLayout from 'layouts/PageLayout';
import TableFilterToolbar from 'components/TableFilterToolbar';
import PropTypes from 'prop-types';
import withTableProps from 'HOC/types/withTable';
import withTable from 'HOC/withTable';
import { getConfirmationModalConfig, resolveApiErrorMessage, transformTableData } from 'utils';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { routesAliases } from 'router/routesAliases';
import { useAsyncFn } from 'react-use';
import { ROLES } from 'appConstants';
import { actions as modalActions } from 'redux/modal';
import { createTrainingModule, deleteTrainingModule, getTrainingModulesList } from 'http/trainingModules';
import getTableConfig from './helpers/getTableConfig';
import TrainingModuleForm from './TrainingModuleForm';

const TRAINING_MODULE_STATUSES = ['active', 'archived'];

const TrainingModules = ({ tableData }) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const { t } = useTranslation('common');
  const role = useSelector(state => state.auth.profile?.role);
  const isHyperAdmin = role === ROLES.HYPER;
  const canManageModules = isHyperAdmin || role === ROLES.SUPER;
  const tableConfig = useMemo(() => getTableConfig(), []);
  const [columns, items] = transformTableData(tableConfig, tableData.data);
  const isLoading = tableData.isFetchingTableData;

  const closeModal = useCallback(() => dispatch(modalActions.closeModal()), [dispatch]);

  const showErrorModal = useCallback(
    message => {
      const [config, element] = getConfirmationModalConfig({
        confirmTitle: t('buttons.ok'),
        confirmAction: closeModal,
        rejectAction: closeModal,
        message: resolveApiErrorMessage(message, {
          t,
          fallbackMessage: t('errors.serverError'),
        }),
      });
      dispatch(modalActions.openModal(config, element));
    },
    [closeModal, dispatch, t],
  );

  const [createState, createModule] = useAsyncFn(
    async payload => {
      const result = await createTrainingModule(payload);
      if (!result.error && !result.statusCode) {
        closeModal();
        history.push(`${routesAliases.editTrainingModule}/${result.id}`);
        return result;
      }
      return result;
    },
    [closeModal, history],
  );

  const [deleteState, removeModule] = useAsyncFn(
    async id => {
      const result = await deleteTrainingModule(id);
      if (!result.error && !result.statusCode) {
        closeModal();
        tableData.handleForceUpdate();
        return result;
      }
      showErrorModal(result.message);
      return result;
    },
    [closeModal, showErrorModal, tableData],
  );

  const openCreateModal = useCallback(() => {
    dispatch(
      modalActions.openModal(
        {
          size: 'medium',
          title: t('trainingModules.addModalTitle'),
        },
        <TrainingModuleForm
          mode="create"
          submitting={createState.loading}
          onCancel={closeModal}
          onSubmitForm={createModule}
        />,
      ),
    );
  }, [closeModal, createModule, createState.loading, dispatch, t]);

  const openEditPage = useCallback(
    moduleId => {
      history.push(`${routesAliases.editTrainingModule}/${moduleId}`);
    },
    [history],
  );

  const handleDeleteModule = useCallback(
    moduleId => {
      const [config, element] = getConfirmationModalConfig({
        title: t('trainingModules.deleteModalTitle'),
        confirmAction: () => removeModule(moduleId),
        rejectAction: closeModal,
        message: t('trainingModules.deleteConfirmationMessage'),
      });
      dispatch(modalActions.openModal(config, element));
    },
    [closeModal, dispatch, removeModule, t],
  );

  const tableActions = useMemo(() => {
    const isDeleteHidden = rowData => role === ROLES.SUPER && rowData.isFromSystem === true;

    const isDeleteDisabled = rowData => isHyperAdmin && rowData.companyId !== 0 && rowData.companyId != null;

    if (!canManageModules) {
      return [
        {
          type: 'icon',
          icon: 'visibility',
          onClick: event => {
            openEditPage(Number(event.currentTarget.dataset.key));
          },
        },
      ];
    }

    return [
      {
        type: 'icon',
        icon: 'edit',
        getIcon: rowData => (role === ROLES.SUPER && rowData.isFromSystem === true ? 'visibility' : 'edit'),
        onClick: event => {
          openEditPage(Number(event.currentTarget.dataset.key));
        },
        getDisabled: rowData => isHyperAdmin && rowData.companyId !== 0 && rowData.companyId != null,
      },
      {
        type: 'icon',
        icon: 'delete',
        color: 'error',
        onClick: event => {
          handleDeleteModule(Number(event.currentTarget.dataset.key));
        },
        getHidden: isDeleteHidden,
        getDisabled: isDeleteDisabled,
      },
    ];
  }, [canManageModules, handleDeleteModule, isHyperAdmin, openEditPage, role]);

  const getRowStyle = rowData => {
    if (role === ROLES.SUPER && rowData.isFromSystem === true) {
      return { backgroundColor: '#f5f5f5' };
    }
    return {};
  };

  const filters = useMemo(() => {
    const baseFilters = [
      {
        type: 'search',
        name: 'search',
        label: t('trainingModules.searchLabel'),
        placeholder: t('trainingModules.searchPlaceholder'),
      },
      {
        type: 'singleSelect',
        name: 'status',
        label: t('tablesLabels.status'),
        options: TRAINING_MODULE_STATUSES.map(value => ({
          value,
          label: t(`trainingModules.statuses.${value}`),
        })),
      },
    ];

    return baseFilters;
  }, [t]);

  const isBusy = isLoading || createState.loading || deleteState.loading;

  return (
    <PageLayout
      pageTitle={t('trainingModules.pageTitle')}
      actionSectionRight={
        canManageModules
          ? [
              {
                type: 'button',
                icon: 'add',
                title: t('trainingModules.actionAdd'),
                onClick: openCreateModal,
              },
            ]
          : []
      }
    >
      <TableFilterToolbar
        filters={filters}
        filtering={tableData.filtering}
        onFilterChange={tableData.handleFiltering}
        onRemoveFilter={tableData.handleRemoveFilter}
        onClearFilters={tableData.handleClearFilters}
      />
      <ModalBusyContainer busy={isBusy}>
        <AppTable
          columns={columns}
          data={items}
          totalCount={tableData.totalCount}
          rowsPerPage={tableData.pageSize}
          onChangePage={tableData.handleChangePage}
          onColumnSorting={tableData.handleSorting}
          currentPage={tableData.page}
          isLazyLoading
          actions={tableActions}
          actionsColumnWidth="120px"
          getRowStyle={getRowStyle}
        />
      </ModalBusyContainer>
    </PageLayout>
  );
};

TrainingModules.propTypes = {
  tableData: PropTypes.shape(withTableProps).isRequired,
};

export default withTable(
  {
    defaultSorting: {
      field: 'id',
      dir: 'desc',
    },
    allowedFilters: ['search', 'status'],
    sortingFieldsMapping: {
      id: 'id',
      name: 'name',
      updatedAt: 'updatedAt',
      code: 'codeSort',
      status: 'statusSort',
    },
    fetchingData: 'items',
  },
  getTrainingModulesList,
)(TrainingModules);
